import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import { TextField } from '@fluentui/react/lib/TextField';
import { DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, Selection } from '@fluentui/react/lib/DetailsList';
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { getCourseTypes, getCourseDates, getCourseDays, addOrUpdateCourseDate, addOrUpdateCourseDay, deleteCourseDate, getPrograms, updateProgramContent, updateCourseContent, createCourseProgramRelation, deleteCourseProgramRelation, getLevels } from "../api";
import { Checkbox, Spinner, CommandBar, DatePicker, DayOfWeek, DefaultButton, Dialog, DialogFooter, DialogType, Dropdown, ICommandBarItemProps, IDropdownOption, Link, Panel, PanelType, PrimaryButton, defaultDatePickerStrings, initializeIcons, Label, Modal, makeStyles, mergeStyles, IconButton } from '@fluentui/react';
import moment from 'moment';
import "moment-timezone";
import { AxiosResponse } from 'axios';
import CourseDetailList from './CourseDetailList';
import CourseActionList from './CourseActionList';
import PricePanel from './PricePanel';
import { useBoolean } from '@fluentui/react-hooks';
import DurationPanel from './DurationPanel';
import ContentPanel from './ContentPanel';
import ProgramContentPanel from './ProgramContentPanel';
import DatePanel from './DatePanel';
import "../styles/DataTable.css";
import "../styles/CourseActionList.css";
import NewCourseTypePanel from './NewCourseTypePanel';
import NewProgramPanel from './NewProgramTypePanel';
import ShownCoursesPanel from './ShownCoursesPanel';
import ProducttypePanel from './ProducttypePanel';
import CourseTypeRelatedsPanel from './CourseTypeRelatedsPanel';
import RequireRole from "./RequireRole";
import MangageImagesPanel from './MangageImagesPanel';
import WASPanel from './WASPanel';
import CourseTypeSettingsPanel from './CourseTypeSettingsPanel';

initializeIcons();

export interface ICourse {
  kursusID: number;
  kursustypeID: number;
  kursustype?: string;
  kursusTitel?: string;
  startDato: Date;
  startDato1Tid?: string;
  startDatoTid?: string;
  slutDato?: Date;
  kursusAdresse?: number;
  program?: string;
  version?: string;
  miljo?: string;
  sprog?: string;
  niveau?: string;
  niveauNr?: number;
  antalDeltagere?: number;
  kursusLokale?: number;
}

export interface ICourseDay {
  kursusdagID: number;
  kursusID: number;
  startDato?: Date;
  slutDato?: Date;
}

export interface ICourseProgram {
  programnavnID: number;
  navn: string;
}

export interface IProgram {
  programnavnID: number;
  navn: string;
  beskrivelse: string;
  beskrivelseCert?: string;
  beskrivelseTests?: string;
  beskrivelseSeminarer?: string;
  beskrivelseAfter?: string;
  programRent?: string;
  programRentAlt?: string;
  kursus: boolean;
  visPåKursusOversigt: boolean;
  test: boolean;
  cert: boolean;
  konsulent: boolean;
  udvikling: boolean;
  programgruppeID: number;
  kursuskategoriID: number;
  produkttypeID: number;
  stopside: boolean;
  inaktiv: boolean;
  faq?: string;
  hvorforProgram?: string;
}

export interface ICourseType {
  kursustypeID: number;
  programnavnID: number;
  sprogID: number;
  afholdelsesformID: number;
  kursuskategoriID: number;
  databladSkabelonID: number;
  niveauID: number;
  kursusTitel: string;
  varighed: number;
  som_firma_kursus: boolean;
  som_specialkursus: boolean;
  som_åbent_kursus: boolean;
  pris: number;
  firmaOsPris?: number;
  firmaJerPris?: number;
  ejRabat: boolean;
  ejKlippekort: boolean;
  kursusmål: string;
  deltagere: string;
  kursusmateriale: string;
  indledning: string;
  inaktiv: boolean;
  indhold?: string;
  spørgeskemaID: number;
  duLaerer: string;
  duFaar: string;
  duBoer: string;
}

export interface IProducttype {
  produkttypeID: number;
  navn: string;
  inaktiv?: boolean;
  sort: number;
  web: boolean;
}

export interface ICourseRelated {
  kursustypeID: number;
  kursustypeID_FoerEfter: number;
  kursustypeFoerEfterID: number;
  sortering: number;
  foerEfter: number;
}

export interface ILevel {
  niveauID: number;
  navn: string;
  inaktiv: boolean;
  niveauNr: number;
}

export interface ISiteMap {
  smid: number;
  parentSMID: number;
  smName: string;
  smFullPath: string;
}

export interface IContent {
  smid: number;
  scid: number;
  coAlwaysShown: boolean;
  coPriority: number;
  coActive: boolean;
  coExclude: boolean;
}

export interface ISharePointContent {
  scid: number;
  scName: string;
  scType: number;
  scDisabled: boolean;
  scContentUrl: string;
}

export function DataTable() {

  const [programColumns, setProgramColumns] = useState<IColumn[]>([]);
  const [programs, setPrograms] = useState<IProgram[]>([]);
  const [filteredPrograms, setFilteredPrograms] = useState<IProgram[]>([]);
  const [program, setProgram] = useState<IProgram | null>(null);

  const [courseTypes, setCourseTypes] = useState<ICourseType[]>([]);
  const [courseType, setCourseType] = useState<ICourseType | null>(null);
  const [courseTypePrice, setCourseTypePrice] = useState<ICourseType | null>(null);
  const [courseTypeDuration, setCourseTypeDuration] = useState<ICourseType | null>(null);
  const [courseTypeContent, setCourseTypeContent] = useState<ICourseType | null>(null);
  const [programContent, setProgramContent] = useState<IProgram | null>(null);
  const [addNewCourseType, setAddNewCourseType] = useState<boolean>(false);
  const [addNewProgram, setAddNewProgram] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filterForInactive, setFilterForInactive] = useState<boolean>(false);
  const [activateProgramDialog, setActivateProgramDialog] = useState<boolean>(false);
  const [isDialogLoading, setIsDialogLoading] = useState<boolean>(false);
  const [token, setToken] = useState<string>("");
  const [dialogProgram, setDialogProgram] = useState<IProgram | null>(null);
  const [shownCourses, setShownCourses] = useState<IProgram | null>(null);
  const [activateCourseDialog, setActivateCourseDialog] = useState<ICourseType | null>(null);
  const [producttypePanel, setProducttypePanel] = useState<boolean>(false);
  const [producttype, setProducttype] = useState<IProducttype | null>(null);
  const [courseTypeRelations, setCourseTypeRelations] = useState<ICourseType | null>(null);
  const [levels, setLevels] = useState<ILevel[]>([]);
  const [imagePanel, setImagePanel] = useState<IProgram | null>(null);
  const [wasPanel, setWASPanel] = useState<boolean>(false);
  const [courseTypeSettings, setCourseTypeSettings] = useState<ICourseType | null>(null);

  const { instance, accounts } = useMsal();

  useEffect(() => {
    setProgramColumns([
      {
        name: "Program", key: "navn", minWidth: 100, fieldName: "navn", onRender: (item) => {
          return (
            <div className='programContainer'>
              <Link className='programLinkText' onClick={() => setProgram(item)}>{item.navn}</Link>
              <div className='btnContainer'>
                {!filterForInactive ? (
                  <>
                    <RequireRole roles={["Admin", "Editor"]}>
                      <DefaultButton className="btnStyle" onClick={() => setImagePanel(item)} > Administrer billede</DefaultButton>
                      <DefaultButton className="btnStyle" onClick={() => { setShownCourses(item) }}>Vælg kurser</DefaultButton>
                      <DefaultButton className="btnStyle" onClick={() => { setProgramContent(item) }}>Administrer indhold</DefaultButton>
                    </RequireRole>
                    <RequireRole roles={["Admin"]}>
                      <IconButton
                        iconProps={{ iconName: 'Delete' }}
                        title="Slet"
                        ariaLabel="slet"
                        onClick={() => { setActivateProgramDialog(true); setDialogProgram(item) }}
                      />
                    </RequireRole>
                  </>
                ) : (
                  <>
                    <RequireRole roles={["Admin", "Editor"]}>
                      <DefaultButton className="btnStyle" onClick={() => setImagePanel(item)} > Administrer billede</DefaultButton>
                      <DefaultButton className="btnStyle" onClick={() => { setShownCourses(item) }}>Vælg kurser</DefaultButton>
                      <DefaultButton className="btnStyle" onClick={() => { setProgramContent(item) }}>Administrer indhold</DefaultButton>
                    </RequireRole>
                    <RequireRole roles={["Editor"]}>
                      <IconButton
                        iconProps={{ iconName: 'Delete' }}
                        title="Slet"
                        ariaLabel="slet"
                        onClick={() => { setActivateProgramDialog(true); setDialogProgram(item) }}
                      />
                    </RequireRole>
                  </>
                )}
              </div>
            </div>
          )
        }
      }
    ]);
  }, [filterForInactive]);

  useEffect(() => {
    setIsLoading(true);
    instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    }).then(async (response) => {
      getPrograms(response.accessToken, filterForInactive, false).then((response) => {
        if (response && response.data) {
          const data: IProgram[] = response.data;
          setPrograms(data);
          setFilteredPrograms(data);
          setIsLoading(false);
        }
      })

      const [levels]: any = await Promise.all([
        getLevels(response.accessToken),
      ]);
      setLevels(levels.data);
    });
  }, [filterForInactive]);

  useEffect(() => {
    instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    }).then((response) => {
      setToken(response.accessToken);
      if (program) {
        getCourseTypes(response.accessToken, program?.programnavnID).then((data: any) => {
          setCourseTypes(data.data);
        })
      }
    });
  }, [program]);

  const _onFilterPrograms = (ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, text: string | undefined): void => {
    console.log(token);
    setFilteredPrograms(text ? programs.filter(i => i.navn.toLowerCase().indexOf(text) > -1) : programs)
  };

  const saveProgram = async (activate: boolean) => {
    setIsDialogLoading(true);
    try {
      if (dialogProgram) {
        const newProgram = {
          ...dialogProgram,
          inaktiv: activate,
        };
        await updateProgramContent(token, newProgram);
      } else {
        console.error("Dialog program is null");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsDialogLoading(false);
      setActivateProgramDialog(false);
    }
  };

  const saveCourse = async (activate: boolean) => {
    setIsDialogLoading(true);
    try {
      if (activateCourseDialog) {
        const newCourse = {
          ...activateCourseDialog,
          inaktiv: activate,
        };
        await updateCourseContent(token, newCourse);
      } else {
        console.error("course is null");
      }
      if (!activate) {
        await createCourseProgramRelation(token, activateCourseDialog?.kursustypeID, activateCourseDialog?.programnavnID);
      }
      else {
        await deleteCourseProgramRelation(token, activateCourseDialog?.kursustypeID, 0);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsDialogLoading(false);
      setActivateCourseDialog(null);
    }
  };


  if (isLoading) {
    return <Spinner label='Indlæser...' />
  }

  return (
    <div>
      {program === null ? (
        <div className='programHeader'>
          <div className='programHeaderFilter'>
            <TextField
              styles={{ root: { width: 200 } }}
              label="Filtrer på program navn"
              onChange={_onFilterPrograms}
            />
            <div>
              <Label>Inaktive programmer</Label>
              <Checkbox
                onChange={(_, checked) => setFilterForInactive(!!checked)}
                checked={filterForInactive}
              />
            </div>
          </div>
          <RequireRole roles={["Admin", "Editor"]}>
            <div className='programBtnContainer'>
              <PrimaryButton className='btnAddCourse' onClick={() => setWASPanel(true)}>Administrer WAS</PrimaryButton>
              <PrimaryButton className='btnAddCourse' onClick={() => setProducttypePanel(true)}>Administrer produktyper</PrimaryButton>
              <PrimaryButton className='btnAddCourse' onClick={() => setAddNewProgram(true)}>Tilføj nyt program</PrimaryButton>
            </div>
          </RequireRole>
        </div>
      ) : (
        <div className='courseTypeHeader'>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '5px' }}>
            <Link onClick={() => {
              setProgram(null)
              setCourseTypes([]);
              setFilteredPrograms(programs);
            }}>Forside </Link>
            <span> / {program.navn}</span>
          </div>
          <RequireRole roles={["Admin", "Editor"]}>
            <div className='programBtnContainer'>
              <PrimaryButton className='btnAddCourse' onClick={() => setAddNewCourseType(true)}>Tilføj nyt kursus</PrimaryButton>
            </div>
          </RequireRole>
        </div>
      )}
      {program === null ?
        (
          <DetailsList
            items={filteredPrograms}
            columns={programColumns}
            setKey="set"
            layoutMode={DetailsListLayoutMode.justified}
            selectionMode={SelectionMode.none}
            selectionPreservedOnEmptyClick={true}
            ariaLabelForSelectionColumn="Toggle selection"
            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
            checkButtonAriaLabel="select row"
          />
        ) : (
          <CourseActionList
            courseTypes={courseTypes}
            levels={levels}
            token={token}
            onClickRelated={setCourseTypeRelations}
            onClickContent={setCourseTypeContent}
            onClickDate={setCourseType}
            onClickPrice={setCourseTypePrice}
            onClickDuration={setCourseTypeDuration}
            onClickDelete={setActivateCourseDialog}
            onClickSettings={setCourseTypeSettings}
          />
        )}
      {imagePanel && (
        <MangageImagesPanel program={imagePanel} onDismiss={() => setImagePanel(null)} />
      )}
      {producttypePanel && filteredPrograms && (
        <ProducttypePanel programs={filteredPrograms} onDismiss={(newProducttype, productProgram, productProgramForSelect, productProgramForImage) => {
          if (newProducttype) { setProducttype(newProducttype); setProducttypePanel(null); setAddNewProgram(true) } else { setProducttypePanel(false) }
          if (productProgram) { setProgramContent(productProgram); setProducttypePanel(null) } else { setProducttypePanel(false) }
          if (productProgramForSelect) { setShownCourses(productProgramForSelect); setProducttypePanel(null) } else { setProducttypePanel(false) }
          if (productProgramForImage) { setImagePanel(productProgramForImage); setProducttypePanel(null) } else { setProducttypePanel(false) }
        }} />
      )}
      {wasPanel && (
        <WASPanel onDismiss={() => setWASPanel(null)} />
      )}
      {shownCourses !== null && (
        <ShownCoursesPanel program={shownCourses} onDismiss={() => setShownCourses(null)} />
      )}
      {addNewProgram && (
        <NewProgramPanel programs={filteredPrograms} producttype={producttype} onDismiss={(newProgram) => { if (newProgram?.programnavnID) { setAddNewProgram(false); setProducttype(null); setAddNewCourseType(true); setProgram(newProgram) } else { setAddNewProgram(false); setProgram(null); setProducttype(null); } }} />
      )}
      {addNewCourseType && program && (
        <NewCourseTypePanel levels={levels} programContent={program} onDismiss={() => setAddNewCourseType(false)} />
      )}
      {programContent !== null && (
        <ProgramContentPanel programContent={programContent} onDismiss={() => setProgramContent(null)} />
      )}
      {courseTypeRelations !== null && (
        <CourseTypeRelatedsPanel courseType={courseTypeRelations} onDismiss={() => setCourseTypeRelations(null)} />
      )}
      {courseTypeContent !== null && program !== null && (
        <ContentPanel courseType={courseTypeContent} program={program} onDismiss={() => setCourseTypeContent(null)} />
      )}
      {courseType !== null && (
        <DatePanel courseType={courseType} onDismiss={() => setCourseType(null)} />
      )}
      {courseTypePrice !== null && program !== null && (
        <PricePanel courseType={courseTypePrice} program={program} onDismiss={() => setCourseTypePrice(null)} />
      )}
      {courseTypeDuration !== null && program !== null && (
        <DurationPanel courseType={courseTypeDuration} program={program} onDismiss={() => setCourseTypeDuration(null)} />
      )}
      {courseTypeSettings !== null && (
        <CourseTypeSettingsPanel
          courseType={courseTypeSettings}
          onDismiss={() => setCourseTypeSettings(null)}
          onSaved={(updatedCourseType) => {
            setCourseTypes((currentCourseTypes) =>
              currentCourseTypes.map((course) =>
                course.kursustypeID === updatedCourseType.kursustypeID
                  ? updatedCourseType
                  : course
              )
            );

            setCourseTypeSettings(updatedCourseType);
          }}
        />
      )}
      <Dialog
        hidden={!activateCourseDialog}
        onDismiss={() => setActivateCourseDialog(null)}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Bekræft deaktivering af kursus",
          subText: "Er du sikker på, at du vil deaktivere dette kursus?",
        }}
        modalProps={{ isBlocking: true }}
      >
        {isDialogLoading ? (
          <Spinner />
        ) : (
          <DialogFooter>
            <PrimaryButton
              text={activateCourseDialog?.inaktiv ? "Ja, aktiver" : "Ja, deaktiver"}
              onClick={() => saveCourse(!activateCourseDialog?.inaktiv)}
              disabled={isDialogLoading}
              styles={{
                root: { backgroundColor: isDialogLoading ? "#f3f3f3" : undefined }
              }}
            />
            <DefaultButton text="Annuller" onClick={() => setActivateCourseDialog(null)} />
          </DialogFooter>
        )}
      </Dialog>
      <Dialog
        hidden={!activateProgramDialog}
        onDismiss={() => setActivateProgramDialog(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: dialogProgram?.inaktiv ? "Bekræft aktivering af program" : "Bekræft deaktivering af program",
          subText: dialogProgram?.inaktiv ? "Er du sikker på, at du vil aktivere dette program?" : "Er du sikker på, at du vil deaktivere dette program?",
        }}
        modalProps={{ isBlocking: true }}
      >
        {isDialogLoading ? (
          <Spinner />
        ) : (
          <DialogFooter>
            <PrimaryButton
              text={dialogProgram?.inaktiv ? "Ja, aktiver" : "Ja, deaktiver"}
              onClick={() => saveProgram(!dialogProgram?.inaktiv)}
              disabled={isDialogLoading}
              styles={{
                root: { backgroundColor: isDialogLoading ? "#f3f3f3" : undefined }
              }}
            />
            <DefaultButton text="Annuller" onClick={() => setActivateProgramDialog(false)} />
          </DialogFooter>
        )}
      </Dialog>
    </div>
  );
}

